import React, { useEffect, useState, useCallback } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Star, ArrowUpCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * PromotionResponseBanner — PO5 (Sprint 2026-08-05)
 *
 * Shown on the talent dashboard when a backup booking has been offered
 * promotion to Primary (backup_promotion_offered_at is set but
 * backup_promotion_confirmed_at is null). Per the revised PO5 rule:
 *   "Do not automatically promote someone without notifying them and
 *    confirming availability."
 *
 * The talent chooses ACCEPT (assignment_type flips to 'primary') or
 * DECLINE (with reason). Both are audited.
 */
const PromotionResponseBanner = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // { booking_id, venue, action }
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/talent/promotions/pending');
      setPending(res.data?.bookings || []);
    } catch {
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!dlg) return;
    if (dlg.action === 'decline' && (!reason || reason.trim().length < 3)) {
      toast.error('A reason is required to decline a promotion offer.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/talent/bookings/${dlg.booking_id}/respond-to-promotion`, {
        accept: dlg.action === 'accept',
        reason: dlg.action === 'decline' ? reason.trim() : undefined,
      });
      toast.success(dlg.action === 'accept'
        ? 'Promotion accepted. You are now Primary — thank you!'
        : 'Promotion declined. Ops has been notified.');
      setDlg(null);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || pending.length === 0) return null;
  const first = pending[0];

  return (
    <>
      <Alert
        className="mb-4 bg-emerald-50 border-emerald-300"
        data-testid="promotion-response-banner"
      >
        <ArrowUpCircle className="h-4 w-4 text-emerald-700" />
        <AlertTitle className="text-emerald-900 font-semibold flex items-center gap-2">
          <Star className="w-4 h-4" />
          You&apos;ve been activated!
        </AlertTitle>
        <AlertDescription className="text-emerald-800 mt-1">
          <div className="text-sm mb-3">
            A primary staff member on <strong>{first.venue_name || first.event_title || 'a shift'}</strong> is
            no longer available and you&apos;re being offered promotion to Primary.
            Please confirm you can work the shift as scheduled.
            {pending.length > 1 && (
              <span className="block text-xs text-emerald-700 mt-1">
                +{pending.length - 1} more pending promotion offer{pending.length > 2 ? 's' : ''}.
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setDlg({ booking_id: first.booking_id || first.id, venue: first.venue_name, action: 'accept' })}
              data-testid="promotion-accept-btn"
            >
              Accept — I&apos;ll work it
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => setDlg({ booking_id: first.booking_id || first.id, venue: first.venue_name, action: 'decline' })}
              data-testid="promotion-decline-btn"
            >
              Decline
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="promotion-response-dialog">
          <DialogHeader>
            <DialogTitle>
              {dlg?.action === 'accept' ? 'Confirm Promotion Acceptance' : 'Decline Promotion'}
            </DialogTitle>
            <DialogDescription>
              {dlg?.action === 'accept'
                ? `You're confirming you can work the full shift at ${dlg?.venue} as the Primary staff member.`
                : `Let ops know why you cannot accept this promotion offer.`}
            </DialogDescription>
          </DialogHeader>
          {dlg?.action === 'decline' && (
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required — 3+ chars"
              rows={3}
              data-testid="promotion-decline-reason"
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDlg(null); setReason(''); }}
              data-testid="promotion-dialog-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className={dlg?.action === 'accept'
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="promotion-dialog-confirm-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {dlg?.action === 'accept' ? 'Accept promotion' : 'Confirm decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PromotionResponseBanner;
